import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, List, ListItem, ListItemText,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Stack, Switch, FormControlLabel,
  Radio, RadioGroup, Checkbox, Divider, Alert, Breadcrumbs, Link,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  ArrowBack as BackIcon,
  NavigateNext as BreadcrumbIcon,
  School as ModuleIcon,
  MenuBook as CourseIcon,
  Quiz as TestIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosInstance from '../../../../utils/axiosInstance';

const TestBuilder = () => {
  const { chatbotId, courseId, moduleId } = useParams();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New state for course and module info
  const [courseInfo, setCourseInfo] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  
  const [testSettings, setTestSettings] = useState({
    title: '',
    instructions: '',
    max_attempts: 3,
    time_limit: 30,
    passing_score: 70,
    show_correct_answers: false,
    randomize_questions: false
  });

  const [questionDialog, setQuestionDialog] = useState({ open: false, question: null });
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    options: [
      { id: 'a', text: '', is_correct: false },
      { id: 'b', text: '', is_correct: false },
      { id: 'c', text: '', is_correct: false },
      { id: 'd', text: '', is_correct: false }
    ],
    correct_answer: '',
    answer_explanation: '',
    points: 1
  });

  useEffect(() => {
    loadCourseAndModuleInfo();
    loadTest();
  }, [moduleId, courseId]);

  // New function to load course and module information
  const loadCourseAndModuleInfo = async () => {
    setLoadingInfo(true);
    try {
      // Load course info
      const courseParams = chatbotId ? { chatbot_id: chatbotId } : {};
      const courseResponse = await axiosInstance.get(`/api/lms/courses/${courseId}`, { params: courseParams });
      if (courseResponse.data.data) {
        setCourseInfo(courseResponse.data.data);
      }

      // Load module info
      const moduleResponse = await axiosInstance.get(`/api/lms/modules/${moduleId}`);
      if (moduleResponse.data.data) {
        setModuleInfo(moduleResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading course/module info:', error);
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadTest = async () => {
    try {
      const response = await axiosInstance.get(`/api/lms/modules/${moduleId}/knowledge-test`, {
        params: { include_questions: true }
      });
      
      if (response.data.data) {
        setTest(response.data.data);
        setTestSettings({
          title: response.data.data.title,
          instructions: response.data.data.instructions || '',
          max_attempts: response.data.data.max_attempts,
          time_limit: response.data.data.time_limit,
          passing_score: response.data.data.passing_score,
          show_correct_answers: response.data.data.show_correct_answers,
          randomize_questions: response.data.data.randomize_questions
        });
        setQuestions(response.data.data.questions || []);
      }
    } catch (error) {
      console.error('Error loading test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTest = async () => {
    try {
      if (test) {
        await axiosInstance.put(`/api/lms/knowledge-tests/${test.id}`, testSettings);
      } else {
        const response = await axiosInstance.post(
          `/api/lms/modules/${moduleId}/knowledge-test`,
          testSettings
        );
        setTest(response.data.data);
      }
      alert('Test settings saved successfully');
    } catch (error) {
      console.error('Error saving test:', error);
      alert('Error saving test settings');
    }
  };

  const handleSaveQuestion = async () => {
    if (!test) {
      alert('Please save test settings first');
      return;
    }

    try {
      if (questionDialog.question) {
        await axiosInstance.put(
          `/api/lms/test-questions/${questionDialog.question.id}`,
          questionForm
        );
      } else {
        await axiosInstance.post(
          `/api/lms/knowledge-tests/${test.id}/questions`,
          questionForm
        );
      }
      
      setQuestionDialog({ open: false, question: null });
      resetQuestionForm();
      loadTest();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Error saving question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await axiosInstance.delete(`/api/lms/test-questions/${questionId}`);
      loadTest();
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleImportQuestions = async () => {
    if (!importFile || !test) return;

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      await axiosInstance.post(
        `/api/lms/knowledge-tests/${test.id}/questions/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      alert('Questions imported successfully');
      setImportDialog(false);
      setImportFile(null);
      loadTest();
    } catch (error) {
      console.error('Error importing questions:', error);
      alert('Error importing questions');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setQuestions(items);

    try {
      await axiosInstance.put(`/api/lms/knowledge-tests/${test.id}/questions/reorder`, {
        order: items.map(q => q.id)
      });
    } catch (error) {
      console.error('Error reordering questions:', error);
      loadTest();
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      question_text: '',
      question_type: 'multiple_choice',
      options: [
        { id: 'a', text: '', is_correct: false },
        { id: 'b', text: '', is_correct: false },
        { id: 'c', text: '', is_correct: false },
        { id: 'd', text: '', is_correct: false }
      ],
      correct_answer: '',
      answer_explanation: '',
      points: 1
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index][field] = value;
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const toggleCorrectOption = (index) => {
    const newOptions = questionForm.options.map((opt, i) => ({
      ...opt,
      is_correct: i === index
    }));
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <IconButton
            onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/builder`)}
            sx={{ mr: 2, mt: 0 }}
          >
            <BackIcon />
          </IconButton>
          <Box>
            {/* Main Title with Course and Module Info */}
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0f172a', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Test Builder
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>

          {/* Import Dialog */}
          {test && (
            <Button variant="outlined" size="small" startIcon={<UploadIcon />} onClick={() => setImportDialog(true)}>
              Import Questions
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSaveTest}>
            Save Test
          </Button>
        </Stack>
      </Box>

      {/* Course & Module Info Card */}
      {!loadingInfo && (courseInfo || moduleInfo) && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CourseIcon sx={{ color: '#4f46e5', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Course
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {courseInfo?.title || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ModuleIcon sx={{ color: '#059669', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Module
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {moduleInfo?.title || 'N/A'}
                </Typography>
              </Box>
            </Box>
            {moduleInfo?.description && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Module Description
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {moduleInfo.description}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* Test Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Test Settings</Typography>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Test Title"
            value={testSettings.title}
            onChange={(e) => setTestSettings({ ...testSettings, title: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Instructions"
            value={testSettings.instructions}
            onChange={(e) => setTestSettings({ ...testSettings, instructions: e.target.value })}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label="Max Attempts"
              value={testSettings.max_attempts}
              onChange={(e) => setTestSettings({ ...testSettings, max_attempts: parseInt(e.target.value) })}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="number"
              label="Time Limit (minutes)"
              value={testSettings.time_limit}
              onChange={(e) => setTestSettings({ ...testSettings, time_limit: parseInt(e.target.value) })}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="number"
              label="Passing Score (%)"
              value={testSettings.passing_score}
              onChange={(e) => setTestSettings({ ...testSettings, passing_score: parseFloat(e.target.value) })}
              inputProps={{ min: 0, max: 100 }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={testSettings.show_correct_answers}
                  onChange={(e) => setTestSettings({ ...testSettings, show_correct_answers: e.target.checked })}
                />
              }
              label="Show Correct Answers After Submission"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={testSettings.randomize_questions}
                  onChange={(e) => setTestSettings({ ...testSettings, randomize_questions: e.target.checked })}
                />
              }
              label="Randomize Question Order"
            />
          </Stack>
        </Stack>
      </Paper>
      
      {test && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Questions ({questions.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetQuestionForm();
                setQuestionDialog({ open: true, question: null });
              }}
            >
              Add Question
            </Button>
          </Box>

          {questions.length === 0 ? (
            <Alert severity="info">No questions yet. Add your first question to get started.</Alert>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {questions.map((question, index) => (
                      <Draggable key={question.id} draggableId={String(question.id)} index={index}>
                        {(provided) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ p: 2, mb: 2 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <Box {...provided.dragHandleProps} sx={{ mr: 2, mt: 1 }}>
                                <DragIcon />
                              </Box>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  Question {index + 1}
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {question.question_text}
                                </Typography>
                                <Chip label={question.question_type} size="small" sx={{ mr: 1 }} />
                                <Chip label={`${question.points} point${question.points !== 1 ? 's' : ''}`} size="small" />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setQuestionForm({
                                    question_text: question.question_text,
                                    question_type: question.question_type,
                                    options: question.options || [],
                                    correct_answer: question.correct_answer || '',
                                    answer_explanation: question.answer_explanation || '',
                                    points: question.points
                                  });
                                  setQuestionDialog({ open: true, question });
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteQuestion(question.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Paper>
      )}

      {/* Question Dialog */}
      <Dialog
        open={questionDialog.open}
        onClose={() => setQuestionDialog({ open: false, question: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {questionDialog.question ? 'Edit Question' : 'Add Question'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Question Text"
              value={questionForm.question_text}
              onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Question Type</InputLabel>
              <Select
                value={questionForm.question_type}
                onChange={(e) => setQuestionForm({ ...questionForm, question_type: e.target.value })}
                label="Question Type"
              >
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="true_false">True/False</MenuItem>
              </Select>
            </FormControl>

            {questionForm.question_type === 'multiple_choice' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Options</Typography>
                {questionForm.options.map((option, index) => (
                  <Box key={option.id} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                    <Radio
                      checked={option.is_correct}
                      onChange={() => toggleCorrectOption(index)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={`Option ${option.id.toUpperCase()}`}
                      value={option.text}
                      onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {questionForm.question_type === 'true_false' && (
              <FormControl>
                <Typography variant="subtitle2" gutterBottom>Correct Answer</Typography>
                <RadioGroup
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="True" />
                  <FormControlLabel value="false" control={<Radio />} label="False" />
                </RadioGroup>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Answer Explanation (Optional)"
              value={questionForm.answer_explanation}
              onChange={(e) => setQuestionForm({ ...questionForm, answer_explanation: e.target.value })}
            />
            <TextField
              type="number"
              label="Points"
              value={questionForm.points}
              onChange={(e) => setQuestionForm({ ...questionForm, points: parseInt(e.target.value) })}
              inputProps={{ min: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialog({ open: false, question: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveQuestion}>
            Save Question
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Questions from CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload a CSV file with columns: question_text, question_type, option_a, option_b, option_c, option_d, correct_answer
          </Typography>
          <Button variant="outlined" component="label" fullWidth>
            Choose File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleImportQuestions}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestBuilder;